import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  MessageSquare,
  Sparkles,
  Tag,
  Zap,
  Undo2,
  Redo2,
  Upload,
  Phone,
  Mail,
  MousePointerClick,
  AlertCircle,
  BellOff,
  BarChart3,
  RefreshCw,
  Download,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import WorkflowEditorPanelShell from './WorkflowEditorPanelShell';
import WriteSmsWithAiModal from './WriteSmsWithAiModal';

const SMS_TEMPLATES = [
  { id: '', label: 'Select template' },
  { id: 'appointment_reminder', label: 'Appointment reminder' },
  { id: 'follow_up', label: 'Follow-up message' },
  { id: 'promo_offer', label: 'Promotional offer' },
];

const SMS_LIMITS = { standard: 160, withEmoji: 70 };

const SMS_STATS_CARDS = [
  { key: 'delivered', label: 'Delivered', icon: Mail },
  { key: 'clicked', label: 'Clicked', icon: MousePointerClick },
  { key: 'failed', label: 'Failed', icon: AlertCircle },
  { key: 'opted_out', label: 'Opted Out', icon: BellOff },
];

function countWords(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function SmsStatisticsDetailsModal({ isOpen, onClose, metric = 'delivered' }) {
  if (!isOpen) return null;

  const label = SMS_STATS_CARDS.find((card) => card.key === metric)?.label || 'SMS';

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-6"
      data-testid="sms-statistics-details-modal"
      data-workflow-editor-ignore-pan
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-900">SMS</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="border-b border-slate-200 px-6 py-3">
          <div className="flex flex-wrap gap-4 text-sm">
            {SMS_STATS_CARDS.map((card) => (
              <span
                key={card.key}
                className={cn(
                  'pb-2',
                  card.key === metric ? 'border-b-2 border-blue-600 font-medium text-blue-600' : 'text-slate-500',
                )}
              >
                {card.label}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-6 py-3">
          <Input readOnly value="2026-06-05 -> 2026-07-05" className="w-[220px]" />
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search contact" className="pl-9" />
          </div>
          <Button variant="outline" size="icon"><RefreshCw size={16} /></Button>
          <Button variant="outline"><Download size={16} className="mr-2" />Export</Button>
        </div>
        <div className="flex-1 overflow-auto px-6 py-10 text-center text-slate-400">
          <BarChart3 size={40} className="mx-auto mb-3 opacity-40" />
          <p>No data for {label.toLowerCase()} yet.</p>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-3 text-sm text-slate-500">
          <span>Total records: 0</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>Previous</Button>
            <span>1</span>
            <Button variant="outline" size="sm" disabled>Next</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const SmsStatisticsPanel = ({ stepId, onViewDetails }) => (
  <div className="space-y-3 p-4" data-testid="sms-statistics-panel">
    {SMS_STATS_CARDS.map(({ key, label, icon: Icon }) => (
      <div key={key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Icon size={18} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{label}</p>
            <p className="text-2xl font-semibold text-slate-900">0%</p>
          </div>
        </div>
        <button
          type="button"
          className="mt-3 text-sm font-medium text-blue-600 hover:underline"
          data-testid={`sms-view-details-${key}`}
          onClick={() => onViewDetails(key)}
        >
          View details
        </button>
      </div>
    ))}
    {!stepId ? (
      <p className="text-xs text-slate-500">Statistics will appear once this workflow sends SMS messages.</p>
    ) : null}
  </div>
);

const SMSNodeEditor = ({
  isOpen,
  onClose,
  step,
  onSave,
  onDelete,
  channelStatus,
  onNavigateToSettings,
  width,
  overlayMode = false,
  onOverlayDismiss,
  activePanelType = 'sms',
  chromeHeight,
  token,
  workflowId,
}) => {
  const [activeTab, setActiveTab] = useState('edit');
  const [showWriteWithAIModal, setShowWriteWithAIModal] = useState(false);
  const [detailsMetric, setDetailsMetric] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [config, setConfig] = useState({
    actionName: 'SMS',
    templateId: '',
    message: '',
    attachments: [],
    attachmentUrl: '',
    fromNumber: '',
    mediaUrl: '',
    deliveryWindow: 'anytime',
    customWindowStart: '09:00',
    customWindowEnd: '17:00',
  });

  useEffect(() => {
    if (!step?.config) return;
    setConfig({
      actionName: step.config.actionName || step.label || step.name || 'SMS',
      templateId: step.config.templateId || '',
      message: step.config.message || '',
      attachments: step.config.attachments || [],
      attachmentUrl: step.config.attachmentUrl || '',
      fromNumber: step.config.fromNumber || '',
      mediaUrl: step.config.mediaUrl || '',
      deliveryWindow: step.config.deliveryWindow || 'anytime',
      customWindowStart: step.config.customWindowStart || '09:00',
      customWindowEnd: step.config.customWindowEnd || '17:00',
    });
    setTestPhone(step.config.testPhone || '');
    setActiveTab('edit');
  }, [step]);

  const messageLength = config.message.length;
  const wordCount = countWords(config.message);
  const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(config.message);
  const segmentLimit = hasEmoji ? SMS_LIMITS.withEmoji : SMS_LIMITS.standard;
  const segments = Math.ceil(messageLength / segmentLimit) || 1;
  const smsChannelReady = channelStatus?.sms?.ready ?? true;

  const handleSave = () => {
    onSave({
      ...step,
      label: config.actionName,
      name: config.actionName,
      config: {
        ...step?.config,
        ...config,
        testPhone,
        configured: Boolean(config.message?.trim()),
      },
    });
    onClose();
  };

  const handleDelete = () => {
    if (step?.id) onDelete?.(step.id);
    onClose();
  };

  const handleSendTestSms = () => {
    if (!testPhone.trim()) {
      toast.error('Enter a test phone number first.');
      return;
    }
    if (!smsChannelReady) {
      toast.info('SMS test sending requires a connected phone service.');
      return;
    }
    toast.info('Test SMS sending is not configured for this environment yet.');
  };

  const insertToken = (tokenValue) => {
    setConfig((prev) => ({ ...prev, message: `${prev.message}${tokenValue}` }));
  };

  if (!isOpen || !step) return null;

  return (
    <>
      <WorkflowEditorPanelShell
        isOpen={isOpen}
        width={width ?? 480}
        overlayMode={overlayMode}
        chromeHeight={chromeHeight}
        onOverlayDismiss={onOverlayDismiss}
        activePanelType={activePanelType}
        className="border-l border-slate-200 bg-white"
        testId="sms-node-editor"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-slate-200 px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">SMS</h3>
                  <p className="text-sm text-slate-500">Sends a text message to the contact</p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-slate-100">
                <X size={18} className="text-slate-500" />
              </button>
            </div>
            <div className="mt-4 flex gap-6 border-b border-slate-200">
              <button
                type="button"
                data-testid="sms-tab-edit"
                className={cn(
                  'pb-3 text-sm font-medium',
                  activeTab === 'edit' ? 'border-b-2 border-blue-600 text-slate-900' : 'text-slate-500',
                )}
                onClick={() => setActiveTab('edit')}
              >
                Edit action
              </button>
              <button
                type="button"
                data-testid="sms-tab-statistics"
                className={cn(
                  'pb-3 text-sm font-medium',
                  activeTab === 'statistics' ? 'border-b-2 border-blue-600 text-slate-900' : 'text-slate-500',
                )}
                onClick={() => setActiveTab('statistics')}
              >
                Statistics
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {activeTab === 'edit' ? (
              <div className="space-y-5 p-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Action name</Label>
                  <Input
                    data-testid="sms-action-name"
                    value={config.actionName}
                    onChange={(e) => setConfig((prev) => ({ ...prev, actionName: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Templates</Label>
                  <select
                    data-testid="sms-template-select"
                    value={config.templateId}
                    onChange={(e) => setConfig((prev) => ({ ...prev, templateId: e.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  >
                    {SMS_TEMPLATES.map((template) => (
                      <option key={template.id || 'empty'} value={template.id}>{template.label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="sms-write-with-ai-button"
                      className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      onClick={() => setShowWriteWithAIModal(true)}
                    >
                      <Sparkles size={14} className="mr-1.5" />
                      Write with AI
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                    <button type="button" className="rounded p-1.5 hover:bg-slate-100" onClick={() => insertToken('{{first_name}}')} aria-label="Insert tag">
                      <Tag size={15} />
                    </button>
                    <button type="button" className="rounded p-1.5 hover:bg-slate-100" aria-label="Insert trigger">
                      <Zap size={15} />
                    </button>
                    <button type="button" className="rounded p-1.5 hover:bg-slate-100" aria-label="Undo">
                      <Undo2 size={15} />
                    </button>
                    <button type="button" className="rounded p-1.5 hover:bg-slate-100" aria-label="Redo">
                      <Redo2 size={15} />
                    </button>
                  </div>
                  <Textarea
                    data-testid="sms-message-input"
                    value={config.message}
                    onChange={(e) => setConfig((prev) => ({ ...prev, message: e.target.value }))}
                    className="min-h-[160px] bg-white"
                    maxLength={480}
                  />
                  <p className="text-xs text-slate-500" data-testid="sms-message-stats">
                    {messageLength} characters | {wordCount} words
                    {messageLength > segmentLimit ? ` | ${segments} segments` : ''}
                  </p>
                </div>

                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attachments</Label>
                  <Button type="button" variant="outline" className="text-blue-600">
                    <Upload size={14} className="mr-2" />
                    Add attachments
                  </Button>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Tag size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="sms-attachment-url"
                        placeholder="Add files through URL"
                        value={config.attachmentUrl}
                        onChange={(e) => setConfig((prev) => ({ ...prev, attachmentUrl: e.target.value }))}
                      />
                    </div>
                    <Button type="button" variant="ghost" className="text-blue-600">+ Add</Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Test phone number</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      data-testid="sms-test-phone"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                    />
                    <button
                      type="button"
                      data-testid="sms-send-test-button"
                      className="whitespace-nowrap text-sm font-medium text-blue-600 hover:underline"
                      onClick={handleSendTestSms}
                    >
                      Send test SMS
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">* Please add country codes along with the numbers.</p>
                </div>
              </div>
            ) : (
              <SmsStatisticsPanel
                stepId={step?.id}
                onViewDetails={setDetailsMetric}
              />
            )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3">
            <Button
              type="button"
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="sms-delete-button"
              onClick={handleDelete}
            >
              Delete
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" data-testid="sms-cancel-button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="sms-save-button"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!config.message.trim()}
                onClick={handleSave}
              >
                Save action
              </Button>
            </div>
          </div>
        </div>
      </WorkflowEditorPanelShell>

      <WriteSmsWithAiModal
        isOpen={showWriteWithAIModal}
        onClose={() => setShowWriteWithAIModal(false)}
        onUseMessage={(message) => setConfig((prev) => ({ ...prev, message }))}
        workflowId={workflowId}
        stepId={step?.id}
        existingMessage={config.message}
        token={token}
      />

      <SmsStatisticsDetailsModal
        isOpen={Boolean(detailsMetric)}
        metric={detailsMetric}
        onClose={() => setDetailsMetric(null)}
      />
    </>
  );
};

export default SMSNodeEditor;
